// navigation/locked-feature-modal.tsx
// Coming-soon / request-access dialog for a locked feature. Frame matches the
// design source; content comes from LOCK_SCREENS. Visible by default (never
// gated on an entrance animation), Esc + overlay click close, focus returns to
// the trigger on close.
import { useEffect, useRef } from "react";
import { Sparkles, Check, X } from "lucide-react";
import { toast } from "sonner";
import { FEATURES, LOCK_SCREENS, type FeatureId } from "./nav-config";

export function LockedFeatureModal({
  feature,
  onClose,
  title: titleOverride,
  desc: descOverride,
  highlights: highlightsOverride,
  ctaLabel,
  toastMessage,
}: {
  feature: FeatureId | null;
  onClose: () => void;
  title?: string;
  desc?: string;
  highlights?: string[];
  ctaLabel?: string;
  toastMessage?: string;
}) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!feature) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Focus the dialog so Tab is scoped here and screen readers announce it.
    cardRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [feature, onClose]);

  if (!feature) return null;

  const meta = LOCK_SCREENS[feature];
  const Icon = FEATURES.find((f) => f.id === feature)?.icon ?? Sparkles;
  const title = titleOverride ?? meta.title;
  const desc = descOverride ?? meta.desc;
  const highlights = highlightsOverride ?? meta.highlights;

  // Nothing is recorded here, so the primary CTA only acknowledges and closes.
  // Callers may still pass an honest toastMessage; without one, no toast.
  const acknowledge = () => {
    if (toastMessage) toast.success(toastMessage, { description: title });
    onClose();
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 60, background: "rgba(16,18,22,0.40)", padding: 16 }}
    >
      <div
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={`${title}: coming soon`}
        onClick={(e) => e.stopPropagation()}
        className="bg-card"
        style={{
          width: 400,
          maxWidth: "92vw",
          border: "1px solid var(--border)",
          borderRadius: 16,
          boxShadow: "var(--elevation-lg)",
          overflow: "hidden",
          outline: "none",
        }}
      >
        {/* Header */}
        <div
          className="relative flex items-center justify-center"
          style={{ height: 148, background: "var(--muted)", borderBottom: "1px solid var(--border)" }}
        >
          <span
            className="inline-flex items-center justify-center bg-card"
            style={{
              width: 60, height: 60, borderRadius: 16,
              border: "1px solid var(--border)", boxShadow: "var(--elevation-sm)",
              color: "var(--signal-blue)",
            }}
          >
            <Icon style={{ width: 26, height: 26, strokeWidth: 1.75 }} />
          </span>
          <span
            className="absolute inline-flex items-center"
            style={{
              top: 12, right: 12, gap: 6, fontSize: 11, fontWeight: 600,
              color: "var(--signal-blue)", background: "var(--signal-blue-10)",
              padding: "4px 9px", borderRadius: 6,
            }}
          >
            <Sparkles style={{ width: 13, height: 13, strokeWidth: 1.75 }} /> Coming soon
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute inline-flex items-center justify-center text-muted-foreground"
            style={{ top: 10, left: 10, width: 28, height: 28, borderRadius: 8, border: "none", background: "transparent", cursor: "pointer" }}
          >
            <X style={{ width: 16, height: 16, strokeWidth: 1.85 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 22px 22px" }}>
          <div className="text-foreground" style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.01em" }}>
            {title}
          </div>
          <p className="text-muted-foreground" style={{ margin: "8px 0 0", fontSize: 13.5, lineHeight: 1.55 }}>
            {desc}
          </p>

          {highlights && (
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              {highlights.map((h) => (
                <div key={h} className="flex items-center text-foreground" style={{ gap: 9, fontSize: 13 }}>
                  <span
                    className="inline-flex items-center justify-center shrink-0"
                    style={{ width: 18, height: 18, borderRadius: 6, background: "var(--alpha-teal-10)", color: "var(--ai-teal-text, #04938F)" }}
                  >
                    <Check style={{ width: 12, height: 12, strokeWidth: 2.2 }} />
                  </span>
                  {h}
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 18, display: "flex", gap: 9 }}>
            <button
              type="button"
              onClick={acknowledge}
              style={{
                flex: 1, padding: 10, borderRadius: 9, border: "none",
                background: "var(--alpha-teal)", color: "#04312F",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}
            >
              {ctaLabel ?? "Got it"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-card text-foreground"
              style={{ padding: "10px 14px", borderRadius: 9, border: "1px solid var(--border)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
